import React, { useState, useEffect, useRef } from 'react';

/**
 * Componente SearchModal
 * Modal de búsqueda reutilizable que permite buscar artículos en toda la app
 * 
 * Props:
 * - isOpen: boolean - Controla si el modal está visible
 * - onClose: function - Callback para cerrar el modal
 * - onArticleClick: function(article, section) - Callback cuando se hace click en un artículo
 * - currentSection: string - Sección actual (opcional, para filtrar búsqueda por sección)
 */
const SearchModal = ({ isOpen, onClose, onArticleClick, currentSection = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchScope, setSearchScope] = useState(currentSection ? 'section' : 'all'); // 'section' o 'all'
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Focus en el input cuando se abre el modal
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Pequeño delay para asegurar que el modal esté visible
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Limpiar cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults(null);
      setHasSearched(false);
      setIsSearching(false);
      setSearchScope(currentSection ? 'section' : 'all');
    }
  }, [isOpen, currentSection]);

  // Buscar con debounce
  useEffect(() => {
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Si no hay query, limpiar resultados
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setHasSearched(false);
      return;
    }

    // Esperar 500ms antes de buscar
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      await performSearch(searchQuery);
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchScope, currentSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Función para realizar la búsqueda
  const performSearch = async (query) => {
    try {
      console.log(`🔍 Buscando: "${query}"`);
      setIsSearching(true);

      // Usar la función global de búsqueda
      const options = {};
      
      // Si el scope es 'section' y hay una sección actual, buscar solo en esa sección
      if (searchScope === 'section' && currentSection) {
        options.section = currentSection;
      }

      const results = await window.buscarArticulos(query, options);
      
      console.log(`✅ Resultados obtenidos:`, results);
      
      setSearchResults(results);
      setHasSearched(true);
      setIsSearching(false);
    } catch (error) {
      console.error('❌ Error en búsqueda:', error);
      setSearchResults({ articles: {}, totalResults: 0, error: true });
      setHasSearched(true);
      setIsSearching(false);
    }
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Función para obtener imagen
  const getImageUrl = (article) => {
    return article.image || article.imageUrl || article.thumbnail || null;
  };

  // Manejar click en artículo
  const handleArticleClick = (article, section) => {
    console.log(`📎 Click en resultado: ${article.title} (${section})`);
    onArticleClick(article, section);
    onClose(); // Cerrar el modal después de seleccionar
  };

  // Preparar artículos para mostrar
  const getArticlesToDisplay = () => {
    if (!searchResults || !searchResults.articles) return [];
    
    const articlesArray = [];
    Object.keys(searchResults.articles).forEach(section => {
      const sectionArticles = searchResults.articles[section];
      if (Array.isArray(sectionArticles)) {
        sectionArticles.forEach(article => {
          articlesArray.push({ ...article, section });
        });
      }
    });
    
    return articlesArray;
  };

  if (!isOpen) return null;

  const articlesToShow = getArticlesToDisplay();

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{ 
        background: 'rgba(0, 0, 0, 0.5)',
        paddingTop: 'max(env(safe-area-inset-top) + 60px, 80px)'
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white w-full mx-4 rounded-3xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col"
        style={{ maxWidth: '600px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header del modal con búsqueda */}
        <div 
          className="text-white px-5 py-5"
          style={{
            background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            {/* Botón cerrar */}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
            >
              ✕
            </button>
            
            <h2 className="text-xl font-bold flex-1 text-center">
              Buscar artículos
            </h2>
            
            {/* Espaciador para centrar el título */}
            <div className="w-9"></div>
          </div>

          {/* Selector de ámbito de búsqueda */}
          {currentSection && (
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => {
                  setSearchScope('section');
                  setSearchResults(null);
                  setHasSearched(false);
                }}
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  searchScope === 'section'
                    ? 'bg-white text-red-600'
                    : 'bg-white/20 text-white/80 hover:bg-white/30'
                }`}
              >
                En {currentSection}
              </button>
              <button
                onClick={() => {
                  setSearchScope('all');
                  setSearchResults(null);
                  setHasSearched(false);
                }}
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  searchScope === 'all'
                    ? 'bg-white text-red-600'
                    : 'bg-white/20 text-white/80 hover:bg-white/30'
                }`}
              >
                En toda la app
              </button>
            </div>
          )}

          {/* Input de búsqueda */}
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Escribe para buscar..."
              className="w-full px-4 py-3 pr-12 rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
              style={{ fontSize: '16px' }} // Evitar zoom en iOS
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              {isSearching ? (
                <div 
                  className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin"
                  style={{ borderTopColor: '#e62c19' }}
                />
              ) : (
                <span className="text-xl">🔍</span>
              )}
            </div>
          </div>

          {/* Info sobre tipo de búsqueda */}
          {searchResults && !isSearching && (
            <div className="mt-3 text-xs text-white/80 text-center">
              {searchScope === 'section' && currentSection && (
                <span className="mr-2">📁 En {currentSection} •</span>
              )}
              {searchScope === 'all' && (
                <span className="mr-2">🌐 En toda la app •</span>
              )}
              {searchResults.totalResults} {searchResults.totalResults === 1 ? 'resultado' : 'resultados'}
              {searchResults.isLocal && <span className="ml-2">(búsqueda local)</span>}
              {searchResults.isLimited && (
                <span className="ml-2">(limitado)</span>
              )}
            </div>
          )}
        </div>

        {/* Resultados */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Estado inicial - Sin búsqueda */}
          {!hasSearched && !isSearching && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-30">🔍</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Busca contenido agustiniano
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed px-6">
                Escribe palabras clave para encontrar evangelios, reflexiones, santos y mucho más
              </p>
            </div>
          )}

          {/* Estado de carga */}
          {isSearching && hasSearched && (
            <div className="text-center py-12">
              <div 
                className="w-12 h-12 border-3 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto mb-4"
                style={{
                  borderWidth: '3px',
                  borderTopColor: '#e62c19'
                }}
              />
              <p className="text-gray-500">Buscando...</p>
            </div>
          )}

          {/* Sin resultados */}
          {hasSearched && !isSearching && articlesToShow.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-30">😔</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No se encontraron resultados
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed px-6">
                Intenta con otras palabras clave o verifica la ortografía
              </p>
            </div>
          )}

          {/* Lista de resultados */}
          {hasSearched && !isSearching && articlesToShow.length > 0 && (
            <div className="space-y-3">
              {articlesToShow.map((article, index) => (
                <div
                  key={article.id || index}
                  onClick={() => handleArticleClick(article, article.section)}
                  className="modern-card cursor-pointer hover:shadow-lg transition-all duration-300"
                  style={{
                    animation: `slideInUp 0.4s ease forwards`,
                    animationDelay: `${index * 0.05}s`
                  }}
                >
                  <div className="flex items-center gap-3 p-4">
                    {/* Imagen o icono */}
                    {getImageUrl(article) ? (
                      <img 
                        src={getImageUrl(article)}
                        alt={article.title}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center text-xl flex-shrink-0" 
                           style={{ background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)', color: 'white' }}>
                        📖
                      </div>
                    )}
                    {/* Fallback icon (hidden by default) */}
                    <div 
                      className="w-16 h-16 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                      style={{ 
                        background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)', 
                        color: 'white',
                        display: getImageUrl(article) ? 'none' : 'flex'
                      }}
                    >
                      📖
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-red-600 font-semibold mb-1">
                        {article.section}
                      </div>
                      <h3 className="text-sm font-bold text-gray-800 mb-1 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      {article.description && (
                        <p className="text-xs text-gray-600 line-clamp-1">
                          {article.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          {formatDate(article.date)}
                        </span>
                        {article.hasAudio && (
                          <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
                            🎵
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;

